import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  RadioGroup,
  FormControlLabel,
  Radio,
  Select,
  MenuItem,
  TextField,
  IconButton,
  Divider,
  FormControl,
  InputLabel,
  Paper,
  Chip,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  AccessTime as TimeIcon,
} from '@mui/icons-material';
import { format } from 'date-fns';
import { ja } from 'date-fns/locale';
import {
  ExtendedStaffShiftData,
  ShiftSchedule,
  ShiftType,
  PresetShiftType,
  PRESET_SHIFTS,
  TIME_OPTIONS,
  // 新構造のインポート
  Appointment,
  AppointmentType,
  BaseWorkingHours,
  AvailabilityInfo,
  APPOINTMENT_CONFIG,
  AvailabilityCalculator,
  updateAvailabilityInfo,
} from '../../types/extended-shift';

interface Staff {
  id: string;
  name: string;
  position: string;
  status: string;
}

interface AdvancedShiftEditDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: (shiftData: ExtendedStaffShiftData) => void;
  staff: Staff;
  date: string; // 'yyyy-MM-dd'
  initialData?: ExtendedStaffShiftData;
}

const AdvancedShiftEditDialog: React.FC<AdvancedShiftEditDialogProps> = ({
  open,
  onClose,
  onSave,
  staff,
  date,
  initialData,
}) => {
  // プリセットタイプ自動判定関数
  const detectPresetType = (hours: BaseWorkingHours): PresetShiftType => {
    if (hours.start === '09:00' && hours.end === '18:00') return 'all_day';
    if (hours.start === '09:00' && hours.end === '13:00') return 'morning';
    if (hours.start === '14:00' && hours.end === '18:00') return 'afternoon';
    return 'custom';  // それ以外は全てカスタム
  };

  // 状態管理（新構造対応）
  const [shiftType, setShiftType] = useState<ShiftType>('work');
  const [presetType, setPresetType] = useState<PresetShiftType>('all_day');
  const [isUserChangingPreset, setIsUserChangingPreset] = useState(false);
  
  // 新構造の状態
  const [baseWorkingHours, setBaseWorkingHours] = useState<BaseWorkingHours>({
    start: '09:00',
    end: '18:00'
  });
  const [appointments, setAppointments] = useState<Appointment[]>([]);
  const [availabilityInfo, setAvailabilityInfo] = useState<AvailabilityInfo | null>(null);
  // TypeScript未使用変数警告回避（保存処理で使用するため保持）
  void availabilityInfo;
  
  // 後方互換性のため旧構造も保持
  const [schedules, setSchedules] = useState<ShiftSchedule[]>([]);
  
  const [holidayInfo, setHolidayInfo] = useState({
    title: '',
    memo: '',
  });

  // データ初期化（新構造対応）
  useEffect(() => {
    if (open) {
      // デバッグログ追加
      console.log('=== AdvancedShiftEditDialog DEBUG ===');
      console.log('open:', open);
      console.log('initialData:', initialData);
      console.log('initialData type:', typeof initialData);
      
      if (initialData) {
        console.log('initialData found - loading existing data');
        setShiftType(initialData.shiftType);
        
        if (initialData.shiftType === 'work' && initialData.workDetails) {
          // 新構造のデータを優先使用
          if (initialData.workDetails.baseWorkingHours) {
            const hours = initialData.workDetails.baseWorkingHours;
            setBaseWorkingHours(hours);
            
            // presetTypeを自動判定（カスタム時間対応）
            const detectedPreset = detectPresetType(hours);
            setPresetType(initialData.workDetails.presetType || detectedPreset);
            
            // 重要: 既存の個別予定を維持
            setAppointments(initialData.workDetails.appointments || []);
            setAvailabilityInfo(initialData.workDetails.availabilityInfo || null);
            console.log('Loaded appointments:', initialData.workDetails.appointments);
          } else if (initialData.workDetails.schedules?.length) {
            // 旧構造からの変換
            const firstSchedule = initialData.workDetails.schedules[0];
            const workingHours: BaseWorkingHours = {
              start: firstSchedule.startTime,
              end: firstSchedule.endTime
            };
            setBaseWorkingHours(workingHours);
            setAppointments([]);
            
            // 空き時間を計算
            const availability = AvailabilityCalculator.calculateAvailability(workingHours, []);
            setAvailabilityInfo(availability);
          }
          
          // 後方互換性のため旧構造も保持
          setSchedules(initialData.workDetails.schedules || []);
        } else {
          setPresetType('all_day');
          const defaultWorkingHours = { start: '09:00', end: '18:00' };
          setBaseWorkingHours(defaultWorkingHours);
          setAppointments([]);
          setAvailabilityInfo(AvailabilityCalculator.calculateAvailability(defaultWorkingHours, []));
          setSchedules([]);
        }
        
        if (initialData.shiftType === 'holiday' && initialData.holidayInfo) {
          setHolidayInfo(initialData.holidayInfo);
        } else {
          setHolidayInfo({ title: '', memo: '' });
        }
      } else {
        // 新規作成時のデフォルト値
        console.log('No initialData - setting default values');
        setShiftType('work');
        setPresetType('all_day');
        const defaultWorkingHours = { start: '09:00', end: '18:00' };
        setBaseWorkingHours(defaultWorkingHours);
        setAppointments([]);
        setAvailabilityInfo(AvailabilityCalculator.calculateAvailability(defaultWorkingHours, []));
        
        // 後方互換性のため旧構造も設定
        setSchedules([{
          id: 'default',
          startTime: '09:00',
          endTime: '18:00',
          title: '通常勤務',
          memo: ''
        }]);
        setHolidayInfo({ title: '', memo: '' });
      }
    }
  }, [open, initialData]);

  // 空き時間自動更新
  useEffect(() => {
    if (shiftType === 'work') {
      const availability = AvailabilityCalculator.calculateAvailability(baseWorkingHours, appointments);
      setAvailabilityInfo(availability);
    }
  }, [baseWorkingHours, appointments, shiftType]);

  // プリセット変更時の処理（新構造対応）
  useEffect(() => {
    // ユーザーが手動でプリセットを変更した場合のみ実行
    if (isUserChangingPreset && shiftType === 'work') {
      const preset = PRESET_SHIFTS[presetType];
      if (presetType !== 'custom' && preset.baseWorkingHours) {
        setBaseWorkingHours(preset.baseWorkingHours);
        setAppointments([]);  // 手動プリセット選択時のみ予定をクリア
      }
      
      // 後方互換性のため旧構造も更新
      if (presetType !== 'custom' && preset.baseWorkingHours) {
        setSchedules([{
          id: `preset-${Date.now()}-${Math.random()}`,
          startTime: preset.baseWorkingHours.start,
          endTime: preset.baseWorkingHours.end,
          title: `${preset.label}勤務`,
          memo: ''
        }]);
      }
      setIsUserChangingPreset(false);  // フラグをリセット
    }
  }, [presetType, shiftType, isUserChangingPreset]);

  // 個別予定管理関数
  const addAppointment = () => {
    const newAppointment: Appointment = {
      id: `appointment-${Date.now()}-${Math.random()}`,
      startTime: baseWorkingHours.start,
      endTime: baseWorkingHours.end,
      type: 'customer',
      title: '施術',
      memo: '',
      priority: 'medium',
    };
    setAppointments(prev => [...prev, newAppointment]);
  };

  const removeAppointment = (id: string) => {
    setAppointments(prev => prev.filter(appointment => appointment.id !== id));
  };

  const updateAppointment = (id: string, updates: Partial<Appointment>) => {
    setAppointments(prev => prev.map(appointment => 
      appointment.id === id ? { ...appointment, ...updates } : appointment
    ));
  };


  // 旧形式関数は開発段階では使用しない（互換性のため保持）
  const removeSchedule = (id: string) => {
    setSchedules(prev => prev.filter(schedule => schedule.id !== id));
  };

  const updateSchedule = (id: string, updates: Partial<ShiftSchedule>) => {
    setSchedules(prev => prev.map(schedule => 
      schedule.id === id ? { ...schedule, ...updates } : schedule
    ));
  };

  // TypeScript未使用警告回避
  void removeSchedule;
  void updateSchedule;

  // 保存処理（新構造対応）
  const handleSave = () => {
    const now = new Date().toISOString();
    const shiftData: ExtendedStaffShiftData = {
      id: initialData?.id || `${staff.id}-${date}`,
      staffId: staff.id,
      date,
      shiftType,
      createdAt: initialData?.createdAt || now,
      updatedAt: now,
    };

    if (shiftType === 'work') {
      // 最新の空き時間情報を計算
      const finalAvailabilityInfo = AvailabilityCalculator.calculateAvailability(baseWorkingHours, appointments);
      
      // presetTypeを最終確認（カスタム時間対応）
      const finalPresetType = detectPresetType(baseWorkingHours);
      console.log('Saving with presetType:', finalPresetType, 'hours:', baseWorkingHours);
      
      shiftData.workDetails = {
        presetType: finalPresetType,  // 自動判定されたプリセットタイプを使用
        baseWorkingHours,           // 新構造：基本勤務時間
        appointments,               // 新構造：個別予定リスト
        availabilityInfo: finalAvailabilityInfo, // 新構造：空き時間情報
        
        // 後方互換性のため旧構造も保存
        schedules,
        workingHours: {
          start: baseWorkingHours.start,
          end: baseWorkingHours.end,
        },
      };
    } else {
      shiftData.holidayInfo = holidayInfo;
    }

    // 空き時間情報を更新してから保存
    const updatedShiftData = updateAvailabilityInfo(shiftData);
    onSave(updatedShiftData);
    onClose();
  };

  // 時刻選択コンポーネント
  const TimeSelect: React.FC<{
    label: string;
    value: string;
    onChange: (value: string) => void;
  }> = ({ label, value, onChange }) => (
    <FormControl size="small" sx={{ minWidth: 80 }}>
      <InputLabel>{label}</InputLabel>
      <Select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        label={label}
        MenuProps={{
          PaperProps: {
            style: {
              maxHeight: 200,
            },
          },
        }}
      >
        {TIME_OPTIONS.map(time => (
          <MenuItem key={time} value={time}>{time}</MenuItem>
        ))}
      </Select>
    </FormControl>
  );

  // 新しいAppointmentCardコンポーネント
  const AppointmentCard: React.FC<{
    appointment: Appointment;
    onUpdate: (updates: Partial<Appointment>) => void;
    onRemove: () => void;
  }> = ({ appointment, onUpdate, onRemove }) => {
    const config = APPOINTMENT_CONFIG[appointment.type];
    
    return (
      <Paper 
        elevation={1} 
        sx={{ 
          p: 2, 
          mb: 2, 
          bgcolor: config.bgColor,
          border: `1px solid ${config.color}20`
        }}
      >
        {/* ヘッダー部分 */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <Typography sx={{ fontSize: '1.2rem' }}>
            {config.icon}
          </Typography>
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>予定タイプ</InputLabel>
            <Select
              value={appointment.type}
              onChange={(e) => onUpdate({ type: e.target.value as AppointmentType })}
              label="予定タイプ"
            >
              {Object.entries(APPOINTMENT_CONFIG).map(([type, config]) => (
                <MenuItem key={type} value={type}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <span>{config.icon}</span>
                    <span>{config.label}</span>
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TimeSelect
            label="開始"
            value={appointment.startTime}
            onChange={(time) => onUpdate({ startTime: time })}
          />
          <Typography variant="body2" color="text.secondary">〜</Typography>
          <TimeSelect
            label="終了"
            value={appointment.endTime}
            onChange={(time) => onUpdate({ endTime: time })}
          />
          <IconButton onClick={onRemove} size="small" color="error">
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Box>

        {/* 詳細入力部分 */}
        <Box sx={{ display: 'flex', gap: 2 }}>
          <TextField
            label="予定タイトル"
            value={appointment.title}
            onChange={(e) => onUpdate({ title: e.target.value })}
            inputProps={{ maxLength: 30 }}
            size="small"
            sx={{ flex: 1 }}
          />
          {appointment.type === 'customer' && (
            <TextField
              label="顧客名"
              value={appointment.customerName || ''}
              onChange={(e) => onUpdate({ customerName: e.target.value })}
              inputProps={{ maxLength: 20 }}
              size="small"
              sx={{ flex: 1 }}
            />
          )}
          <TextField
            label="メモ"
            value={appointment.memo}
            onChange={(e) => onUpdate({ memo: e.target.value })}
            inputProps={{ maxLength: 100 }}
            size="small"
            sx={{ flex: 2 }}
          />
        </Box>
      </Paper>
    );
  };

  // 旧予定カードコンポーネント（後方互換性のため保持）
  const ScheduleCard: React.FC<{
    schedule: ShiftSchedule;
    onUpdate: (updates: Partial<ShiftSchedule>) => void;
    onRemove: () => void;
    canRemove: boolean;
  }> = ({ schedule, onUpdate, onRemove, canRemove }) => (
    <Paper elevation={1} sx={{ p: 2, mb: 2, bgcolor: 'grey.50' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <TimeIcon color="action" fontSize="small" />
        <TimeSelect
          label="開始"
          value={schedule.startTime}
          onChange={(time) => onUpdate({ startTime: time })}
        />
        <Typography variant="body2" color="text.secondary">〜</Typography>
        <TimeSelect
          label="終了"
          value={schedule.endTime}
          onChange={(time) => onUpdate({ endTime: time })}
        />
        {canRemove && (
          <IconButton onClick={onRemove} size="small" color="error">
            <DeleteIcon fontSize="small" />
          </IconButton>
        )}
      </Box>
      <Box sx={{ display: 'flex', gap: 2 }}>
        <TextField
          label="予定タイトル"
          value={schedule.title}
          onChange={(e) => onUpdate({ title: e.target.value })}
          inputProps={{ maxLength: 30 }}
          size="small"
          sx={{ flex: 1 }}
        />
        <TextField
          label="メモ"
          value={schedule.memo}
          onChange={(e) => onUpdate({ memo: e.target.value })}
          inputProps={{ maxLength: 100 }}
          size="small"
          sx={{ flex: 2 }}
        />
      </Box>
    </Paper>
  );
  
  // TypeScript未使用警告回避
  void ScheduleCard;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography variant="h6">シフト設定</Typography>
          <Chip 
            label={`${staff.name} - ${format(new Date(date), 'M月d日(E)', { locale: ja })}`}
            color="primary"
            variant="outlined"
            size="small"
          />
        </Box>
      </DialogTitle>

      <DialogContent>
        <Box sx={{ pt: 1 }}>
          {/* シフトタイプ選択 */}
          <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 'medium' }}>
            勤務タイプ
          </Typography>
          
          <RadioGroup
            value={shiftType}
            onChange={(e) => setShiftType(e.target.value as ShiftType)}
            sx={{ mb: 3 }}
          >
            <FormControlLabel 
              value="work" 
              control={<Radio />} 
              label="出勤"
            />
            <FormControlLabel 
              value="holiday" 
              control={<Radio />} 
              label="休日"
            />
          </RadioGroup>

          <Divider sx={{ my: 3 }} />

          {/* 出勤時の設定（新構造対応） */}
          {shiftType === 'work' && (
            <Box>
              {/* プリセット選択 */}
              <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 'medium' }}>
                勤務設定
              </Typography>
              <FormControl fullWidth sx={{ mb: 3 }}>
                <InputLabel>シフトタイプ</InputLabel>
                <Select
                  value={presetType}
                  onChange={(e) => {
                    setIsUserChangingPreset(true);  // ユーザー変更フラグを立てる
                    setPresetType(e.target.value as PresetShiftType);
                  }}
                  label="シフトタイプ"
                >
                  {Object.entries(PRESET_SHIFTS).map(([key, preset]) => (
                    <MenuItem key={key} value={key}>{preset.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>

              {/* 基本勤務時間設定 */}
              <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 'medium' }}>
                基本勤務時間（予約受付可能時間）
              </Typography>
              <Paper elevation={1} sx={{ p: 2, mb: 3, bgcolor: 'primary.50' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <TimeSelect
                    label="勤務開始"
                    value={baseWorkingHours.start}
                    onChange={(time) => {
                      setBaseWorkingHours(prev => ({ ...prev, start: time }));
                      // カスタム時間設定時は自動的にcustomタイプに
                      const newHours = { ...baseWorkingHours, start: time };
                      setPresetType(detectPresetType(newHours));
                    }}
                  />
                  <Typography variant="body2" color="text.secondary">〜</Typography>
                  <TimeSelect
                    label="勤務終了"
                    value={baseWorkingHours.end}
                    onChange={(time) => {
                      setBaseWorkingHours(prev => ({ ...prev, end: time }));
                      // カスタム時間設定時は自動的にcustomタイプに
                      const newHours = { ...baseWorkingHours, end: time };
                      setPresetType(detectPresetType(newHours));
                    }}
                  />
                  <Typography variant="body2" color="primary" sx={{ ml: 2 }}>
                    この時間内で予約を受け付けます
                  </Typography>
                </Box>
              </Paper>


              {/* 個別予定管理 */}
              <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 'medium' }}>
                個別予定管理
              </Typography>

              {appointments.map((appointment) => (
                <AppointmentCard
                  key={appointment.id}
                  appointment={appointment}
                  onUpdate={(updates) => updateAppointment(appointment.id, updates)}
                  onRemove={() => removeAppointment(appointment.id)}
                />
              ))}

              {/* 予定追加ボタン */}
              <Button
                startIcon={<AddIcon />}
                onClick={addAppointment}
                variant="outlined"
                color="primary"
                sx={{ mb: 2 }}
                fullWidth
              >
                個別予定を追加
              </Button>

              {/* 旧構造表示は開発段階では非表示 */}
            </Box>
          )}

          {/* 休日時の設定 */}
          {shiftType === 'holiday' && (
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 'medium' }}>
                休日詳細
              </Typography>

              <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField
                  label="休日タイトル"
                  value={holidayInfo.title}
                  onChange={(e) => setHolidayInfo(prev => ({ ...prev, title: e.target.value }))}
                  inputProps={{ maxLength: 30 }}
                  placeholder="休暇、研修、体調不良など"
                  sx={{ flex: 1 }}
                />
                <TextField
                  label="メモ"
                  value={holidayInfo.memo}
                  onChange={(e) => setHolidayInfo(prev => ({ ...prev, memo: e.target.value }))}
                  inputProps={{ maxLength: 100 }}
                  placeholder="詳細情報があれば記載"
                  sx={{ flex: 2 }}
                />
              </Box>
            </Box>
          )}
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 3 }}>
        <Button onClick={onClose} variant="outlined">
          キャンセル
        </Button>
        <Button onClick={handleSave} variant="contained">
          保存
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AdvancedShiftEditDialog;